import { GridColumnVisibilityModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectProfile } from 'src/modules/profile/slices';
import { useGetContactCustomFieldsApi } from 'src/api/customFields';
import { ContactDefaultCustomField, User } from 'src/constants/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import {
  useDynamicGroupRulesFormDialogController,
  useGroupFormDialogController,
} from 'src/modules/groups/controllers';
import { buildFormData } from 'src/modules/groups/helpers';
import { useGroupService } from 'src/modules/groups/services';
import { Group, GroupFormData } from 'src/modules/groups/types';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { useContactService, useContactsService } from '../services';
import { useAddContactsToGroupDialogController } from './AddContactsToGroupDialogController';
import { useGetUsersApi } from 'src/api/users';
import { useGroupsApi } from 'src/modules/groups/api/GroupsApi';
import { buildCustomFieldVisibilityFilter } from '../helpers/customFieldVisibility';
import {
  buildRestoredContactsFilter,
  clearContactsTableQuery,
  loadContactsTableState,
  saveContactsTableQuery,
  saveContactsTableState,
} from '../helpers/contactsTableStorage';

type Props = {
  groupId?: string;
  isSpam?: boolean;
  isInvalidContacts?: boolean;
  isNew?: boolean;
  openDynamicRules?: boolean;
};

export const useContactsViewController = ({ groupId, isSpam, isInvalidContacts, isNew, openDynamicRules }: Props) => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const { profile } = useSelector(selectProfile);
  const currentUserId = profile?.id;
  const currentUserIdRef = useRef(currentUserId);
  currentUserIdRef.current = currentUserId;

  const [customFields, setCustomFields] = useState<ContactDefaultCustomField[] | undefined>();
  const [initialColumnVisibility, setInitialColumnVisibility] = useState<
    GridColumnVisibilityModel | undefined
  >();
  const [groups, setGroups] = useState<Group[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  
  const getCustomFields = useGetContactCustomFieldsApi();
  const getUsers = useGetUsersApi();
  const { getGroups } = useGroupsApi();

  const {
    loading: contactsLoading,
    filter: contactsFilter,
    list: contactList,
    getContacts,
    changeContactsFilter,
    resetContacts,
  } = useContactsService();
  const { deleteContact } = useContactService();
  const {
    loading: groupLoading,
    group,
    getGroup,
    getDefaultGroup,
    removeContactsFromGroup,
    resetGroup,
  } = useGroupService();

  const groupFormDialogController = useGroupFormDialogController();
  const addContactsToGroupDialogController = useAddContactsToGroupDialogController();
  const dynamicGroupRulesFormDialogController = useDynamicGroupRulesFormDialogController();

  const initLoad = useCallback(async () => {
    try {
      const [customFields, groups, group, users] = await Promise.all([
        await getCustomFields({ companyId: currentCompanyId }),
        await getGroups({
          companyId: currentCompanyId,
          limit: 10000,
          offset: 0,
          statuses: 'active,archived',
        }),
        groupId
          ? await getGroup(currentCompanyId, groupId)
          : await getDefaultGroup(currentCompanyId),
        await getUsers({ companyId: currentCompanyId, limit: 10000, offset: 0 }),
      ]);

      setCustomFields(customFields.data.items);
      setGroups(groups.data.items);
      setUsers(users.data.items);

      const savedTableState = loadContactsTableState(currentUserId);
      setInitialColumnVisibility(savedTableState.columnVisibility);

      changeContactsFilter({
        isSpam: !!isSpam,
        groups: group.id,
        validStatus: isInvalidContacts ? 'invalid' : undefined,
        ...buildRestoredContactsFilter(savedTableState, customFields.data.items),
      });
      await getContacts(currentCompanyId);

      if (group?.type === 'custom' && isNew) {
        addContactsToGroupDialogController.open(group.id);
      }

      if (group?.type === 'dynamic' && (isNew || openDynamicRules)) {
        dynamicGroupRulesFormDialogController.open(group);
      }
    } catch (e) {
      console.log(e);
    }
  }, [groupId, isSpam, isInvalidContacts, isNew, openDynamicRules, currentCompanyId, currentUserId, getCustomFields, getGroups, getGroup, getDefaultGroup, getUsers, changeContactsFilter, getContacts, addContactsToGroupDialogController, dynamicGroupRulesFormDialogController]);

  const reloadContacts = useCallback(async () => {
    try {
      await getContacts(currentCompanyId);
    } catch (e) {
      console.log(e);
    }
  }, [getContacts, currentCompanyId]);

  const reloadContactsDebounce = useMemo(() => debounce(reloadContacts, 500), [reloadContacts]);

  const changeContactFilterForm = useCallback(
    async (filter: Partial<RootState['contacts']['filter']>) => {
      changeContactsFilter(filter);
      await reloadContacts();
    },
    [changeContactsFilter, reloadContacts],
  );

  const changeContactsSearchFilter = useCallback(
    async (_: ChangeEvent<HTMLInputElement>, query: string) => {
      changeContactsFilter({ query });
      saveContactsTableQuery(currentUserId, query);
      await reloadContactsDebounce();
    },
    [changeContactsFilter, currentUserId, reloadContactsDebounce],
  );

  const changeContactsSorting = useCallback(async (s: GridSortModel) => {
    const nextSort = s[0];

    let sortFilter: Pick<RootState['contacts']['filter'], 'sort' | 'sortMetadataId' | 'sortOrder'>;

    if (!nextSort?.sort) {
      sortFilter = { sort: undefined, sortMetadataId: null, sortOrder: undefined };
    } else if (customFields?.some(field => field.metadataId === nextSort.field)) {
      sortFilter = {
        sort: undefined,
        sortMetadataId: nextSort.field,
        sortOrder: nextSort.sort,
      };
    } else {
      sortFilter = {
        sort: `${nextSort.field}_${nextSort.sort}`,
        sortMetadataId: null,
        sortOrder: undefined,
      };
    }

    changeContactsFilter(sortFilter);
    saveContactsTableState(currentUserId, sortFilter);
    await reloadContacts();
  }, [customFields, changeContactsFilter, currentUserId, reloadContacts]);

  const changeContactsColumnVisibility = useCallback(async (model: GridColumnVisibilityModel) => {
    if (!customFields) return;

    const visibilityFilter = buildCustomFieldVisibilityFilter(
      customFields,
      model,
      contactsFilter.sortMetadataId,
    );

    changeContactsFilter(visibilityFilter);
    saveContactsTableState(currentUserId, {
      columnVisibility: model,
      ...(visibilityFilter.sortMetadataId === null
        ? { sortMetadataId: null, sortOrder: undefined }
        : {}),
    });
    await reloadContacts();
  }, [customFields, contactsFilter.sortMetadataId, changeContactsFilter, currentUserId, reloadContacts]);

  const changeContactsPagination = useCallback(async (p: GridPaginationModel) => {
    changeContactsFilter({ page: p.page, limit: p.pageSize, offset: p.page * p.pageSize });
    await reloadContacts();
  }, [changeContactsFilter, reloadContacts]);

  const removeContact = useCallback(
    async (contactId: string) => {
      if (group) {
        try {
          if (group.type === 'default') {
            await deleteContact(currentCompanyId, contactId);
          } else {
            await removeContactsFromGroup(currentCompanyId, group.id, [contactId]);
          }

          await getContacts(currentCompanyId);
        } catch (e) {
          console.log(e);
        }
      }
    },
    [group, deleteContact, currentCompanyId, removeContactsFromGroup, getContacts],
  );

  useEffect(() => {
    initLoad();

    return () => {
      resetGroup();
      resetContacts();
      // The saved query must only survive a page refresh (no cleanup runs
      // then) — leaving the page within the SPA drops it.
      clearContactsTableQuery(currentUserIdRef.current);
    };
  }, [groupId, isSpam, isInvalidContacts]);

  return {
    isPageLoading: contactsLoading.getList || groupLoading.getById,
    currentCompanyId,
    groups,
    group,
    users,
    customFields,
    initialColumnVisibility,
    contactsFilter,
    contactList,
    reloadContacts,
    changeContactFilterForm,
    changeContactsSearchFilter,
    changeContactsSorting,
    changeContactsColumnVisibility,
    changeContactsPagination,
    removeContact,
    groupFormDialogController: {
      ...groupFormDialogController,
      submit: (full: GroupFormData, dirty: Partial<GroupFormData>) =>
        groupFormDialogController.submit(full, dirty, () =>
          groupId ? getGroup(currentCompanyId, groupId) : getDefaultGroup(currentCompanyId),
        ),
    },
    addContactsToGroupDialogController: {
      ...addContactsToGroupDialogController,
      submit: (contactIds: string[]) =>
        addContactsToGroupDialogController.submit(contactIds, () => getContacts(currentCompanyId)),
    },
    dynamicGroupRulesFormDialogController: {
      ...dynamicGroupRulesFormDialogController,
      submit: (full: GroupFormData, dirty: Partial<GroupFormData>) =>
        dynamicGroupRulesFormDialogController.submit(full, dirty, async updatedGroup => {
          groupFormDialogController.syncRules(buildFormData(updatedGroup).rules || []);
          await reloadContacts();
        }),
    },
  };
};
